import React, { useEffect, useMemo, useState } from 'react';
import { collapseStrategies } from '../../utils/optionStrategies';
import { fetchClientPositions, fetchClientTrades } from '../../services/vectorDashboardApi';
import { fmtCurrency, sentimentClass } from './clientReportingFormat';
import { brokerAccountDisplayName } from './clientReportingUtils';
import { monthKeyOf, monthLabel, monthsWithTrades, yearsWithTrades } from './tradeCalendar';
import TradeMonthNavigator from './TradeMonthNavigator';
import PositionReconciliationSummary, { OpenPositionsCompact, positionsSyncNote } from './PositionReconciliation';
import ClientReportingConnectPrompt from './ClientReportingConnectPrompt';
import { CLIENT_REPORTING_DEMO_TRADES } from './clientReportingDemoPreview';

const RECENT_LIMIT = 12;

function formatTradeDate(dateStr) {
  const [y, m, d] = String(dateStr || '').split('-');
  if (!y || !m || !d) return dateStr || '—';
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
}

function recentTradesEmptyMessage({ loading, error, activeClient, summary, monthKey }) {
  if (loading) return 'Loading trades…';
  if (error) return error;
  if (!activeClient) return 'Add an account to see recent trades.';

  const hasBalance = summary?.equity?.total != null && Number.isFinite(Number(summary.equity.total));
  const isNinja = activeClient.broker === 'Ninja Trader';
  const monthLabelText = monthKey ? monthLabel(monthKey) : '';

  if (monthLabelText) {
    return `No trades in ${monthLabelText}.`;
  }

  if (hasBalance && isNinja) {
    return 'Account connected and balance synced, but Tradovate returned no closed trades yet. Use Add / Sync Trades to refresh, or confirm the trade is closed in NinjaTrader.';
  }
  if (hasBalance) {
    return 'Account connected, but no closed trades synced yet.';
  }
  return 'No trades yet. Connect a broker and sync to see activity here.';
}

function TradesTable({ trades }) {
  return (
    <table className="vd-ml-recent-trades-table">
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col">Symbol</th>
          <th scope="col">Side</th>
          <th scope="col" className="vd-num">Qty</th>
          <th scope="col" className="vd-num">P&amp;L</th>
        </tr>
      </thead>
      <tbody>
        {trades.map((trade, index) => {
          const profit = Number(trade.profit);
          const tone = sentimentClass(profit);
          const key = `${trade.exitDate || trade.entryDate}-${trade.symbol}-${trade.side}-${index}`;
          return (
            <tr key={key}>
              <td>{formatTradeDate(trade.exitDate || trade.entryDate)}</td>
              <td>
                {trade.symbol || '—'}
                {trade.legsText ? <small className="vd-strategy-legs">{trade.legsText}</small> : null}
              </td>
              <td>{trade.sideLabel || trade.side || '—'}</td>
              <td className="vd-num">{trade.isStrategy ? `${trade.legs.length} legs` : (trade.quantity ?? '—')}</td>
              <td className={`vd-num ${tone}`}>{fmtCurrency(profit, { signed: true })}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function ClientReportingRecentTrades({
  clients = [],
  selectedClientId = null,
  refreshKey = 0,
  summary = null,
  monthKey = null,
  onMonthKeyChange = null,
  showDemoPreview = false,
  onConnectAction = null,
  connectPromptTitle = 'Add your account',
  connectPromptMessage = 'Connect a brokerage to see your recent trades and reconciliation here.',
  connectPromptActionLabel = 'Add / Sync Trades',
}) {
  const [trades, setTrades] = useState([]);
  const [positionsPayload, setPositionsPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const activeClient = useMemo(() => {
    if (selectedClientId && clients.some((c) => c.id === selectedClientId)) {
      return clients.find((c) => c.id === selectedClientId);
    }
    return clients[0] || null;
  }, [clients, selectedClientId]);

  useEffect(() => {
    let cancelled = false;

    async function loadTrades() {
      if (showDemoPreview) {
        setTrades(CLIENT_REPORTING_DEMO_TRADES);
        setPositionsPayload(null);
        setLoading(false);
        setError('');
        return;
      }

      if (!activeClient?.dataSeriesName) {
        setTrades([]);
        setPositionsPayload(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const [tradesRes, positionsRes] = await Promise.all([
          fetchClientTrades(activeClient.dataSeriesName, 'All'),
          fetchClientPositions(activeClient.dataSeriesName, { monthKey }),
        ]);
        if (cancelled) return;
        setTrades(collapseStrategies(tradesRes.trades || []));
        setPositionsPayload(positionsRes);
      } catch (err) {
        if (!cancelled) {
          console.error('[client-reporting-recent-trades]', err);
          setTrades([]);
          setPositionsPayload(null);
          setError(err.message || 'Failed to load trades.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTrades();
    return () => { cancelled = true; };
  }, [activeClient, refreshKey, monthKey, showDemoPreview]);

  const visibleTrades = useMemo(() => {
    const dated = (trade) => trade.exitDate || trade.entryDate || trade.date;
    let list = trades;
    if (monthKey) {
      list = trades.filter((trade) => monthKeyOf(dated(trade)) === monthKey);
    }
    const sorted = list
      .slice()
      .sort((a, b) => String(dated(b)).localeCompare(String(dated(a))));
    return monthKey
      ? sorted
      : sorted.slice(0, RECENT_LIMIT);
  }, [trades, monthKey]);

  const months = useMemo(() => monthsWithTrades(trades), [trades]);
  const years = useMemo(() => yearsWithTrades(trades), [trades]);
  const accountLabel = activeClient ? brokerAccountDisplayName(activeClient) : '';
  const showNavigator = Boolean(onMonthKeyChange && trades.length && !showDemoPreview);
  const openPositions = positionsPayload?.supportsOpenPositions ? (positionsPayload.positions || []) : [];
  const reconciliation = positionsPayload?.reconciliation || null;
  const monthLabelText = monthKey ? monthLabel(monthKey) : '';
  const showPositionsBlock = Boolean(
    !showDemoPreview
    && positionsPayload?.supportsOpenPositions
    && reconciliation,
  );
  const isDemoPreview = showDemoPreview && !loading;
  const demoTrades = isDemoPreview ? CLIENT_REPORTING_DEMO_TRADES.slice(0, RECENT_LIMIT) : visibleTrades;

  return (
    <section className="vd-panel vd-ml-widget vd-ml-recent-trades">
      <div className="vd-ml-widget-header vd-ml-recent-trades-header">
        <div className="vd-ml-recent-trades-header-main">
          <h3>{showNavigator ? 'Trades' : 'Recent Trades'}</h3>
          {showNavigator ? (
            <TradeMonthNavigator
              monthKey={monthKey}
              onMonthKeyChange={onMonthKeyChange}
              monthsWithData={months}
              yearsWithData={years}
            />
          ) : null}
        </div>
        {accountLabel ? (
          <span className="vd-ml-widget-subtitle">{accountLabel}</span>
        ) : null}
      </div>

      <div className={`vd-ml-recent-trades-wrap${loading ? ' vd-ml-widget-loading' : ''}${isDemoPreview ? ' is-demo-preview' : ''}`}>
        {isDemoPreview ? (
          <div className="vd-cr-connect-overlay vd-cr-connect-overlay--inset">
            <ClientReportingConnectPrompt
              title={connectPromptTitle}
              message={connectPromptMessage}
              actionLabel={connectPromptActionLabel}
              onAction={onConnectAction}
              compact
            />
          </div>
        ) : null}

        {demoTrades.length ? (
          <TradesTable trades={demoTrades} />
        ) : (
          <p className="vd-ml-widget-empty">
            {recentTradesEmptyMessage({ loading, error, activeClient, summary, monthKey })}
          </p>
        )}

        {showPositionsBlock ? (
          <div className="vd-ml-recent-trades-positions-block">
            {!openPositions.length && positionsPayload?.supportsPerSymbolPositions !== false ? (
              <p className="vd-open-positions-empty-note">
                {positionsSyncNote(
                  positionsPayload?.broker,
                  positionsPayload?.supportsPerSymbolPositions,
                )}
              </p>
            ) : null}
            <OpenPositionsCompact
              positions={openPositions}
              asOf={positionsPayload?.asOf}
            />
            <PositionReconciliationSummary
              reconciliation={reconciliation}
              monthKey={monthKey}
              monthLabelText={monthLabelText}
              broker={positionsPayload?.broker}
              supportsPerSymbolPositions={positionsPayload?.supportsPerSymbolPositions}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
